/**
 * Browser data layer for campaign participant automation.
 *
 * Reads (flow, participants, events) go straight through the authenticated
 * browser Supabase client, gated by RLS (org membership through the parent
 * campaign). Writes go through the service-role Pages Functions under
 * `/api/automation/*`, which re-assert edit authorization in code; each write
 * wrapper takes the caller's Supabase access token for the bearer header.
 *
 * Row shapes are hand-typed here because the repo has no generated DB types.
 * They mirror the base columns declared in
 * `20260712105500_campaign_automation.sql`. Worker-only delivery fencing is
 * added by the later campaign automation hardening migrations.
 *
 * @module services/automationService
 */
import { getSupabase } from '../lib/supabase';
import type { AutomationBlock } from '../lib/api-contracts/automation-blocks';

/** Module-level singleton browser client (RLS-scoped). */
const supabase = getSupabase();

/** A `campaign_automation_flows` row. */
export interface AutomationFlowRow {
  /** Flow id. */
  id: string;
  /** Parent campaign (interview_sessions.id). */
  campaign_id: string;
  /** Whether the executor sweep processes this flow. */
  enabled: boolean;
  /** The validated automation sequence, stored verbatim. */
  blocks: AutomationBlock[];
  /** Compare-and-swap version, bumped on every blocks write. */
  version: number;
  /** Fallback IANA timezone (the enabling admin's browser zone). */
  default_timezone: string;
  /** Row creation timestamp (ISO). */
  created_at: string;
  /** Row update timestamp (ISO). */
  updated_at: string;
  /** State transition committed by the enable endpoint, when present. */
  operation?: {
    /** Stable caller-owned operation id. */
    id: string;
    /** A flow row is returned only after durable completion. */
    status: 'completed';
    /** Whether this request created the flow instead of re-enabling it. */
    created: boolean;
    /** Never-contacted participants made due by this request. */
    activated_count: number;
    /** Whether this response reconciled an earlier completed request. */
    idempotent: boolean;
  };
}

/** Non-failure acknowledgement used while an enable outcome is unresolved. */
export interface AutomationEnableUnconfirmed {
  /** Stable machine-readable acknowledgement state. */
  error_code: 'operation_unconfirmed';
  /** Operation that the UI must poll without reverting optimistic state. */
  operation: { id: string; status: 'unconfirmed' };
}

/** Durable cancellation returned only after mutation is proven inert. */
export interface AutomationEnableCancelled {
  /** Stable terminal error code. */
  error_code: 'operation_cancelled';
  /** Operation identity retained across cancellation and status reads. */
  operation: { id: string; status: 'cancelled' };
}

/** Completed flow or a still-unresolved operation acknowledgement. */
export type AutomationEnableResult =
  | AutomationFlowRow
  | AutomationEnableUnconfirmed
  | AutomationEnableCancelled;

/** A `campaign_participants` row. */
export interface CampaignParticipantRow {
  /** Participant id. */
  id: string;
  /** Parent campaign id. */
  campaign_id: string;
  /** Linked response id, once the participant starts a conversation. */
  interview_data_id: string | null;
  /** Participant email (the dedupe key, case-insensitive per campaign). */
  email: string;
  /** Participant name, when provided. */
  name: string | null;
  /** Participant company, when provided. */
  company: string | null;
  /** Participant phone, when provided. */
  phone: string | null;
  /** Participant IANA timezone, when provided and valid. */
  timezone: string | null;
  /** Funnel status. */
  status: 'added' | 'invited' | 'started' | 'completed' | 'delivery_issue';
  /** Unsubscribe timestamp (ISO), separate from status; null when subscribed. */
  unsubscribed_at: string | null;
  /** Whether automation is paused for this participant. */
  automation_paused: boolean;
  /** The block the participant is currently positioned on, if any. */
  current_block_id: string | null;
  /** When the next action is due (ISO), if scheduled. */
  next_action_at: string | null;
  /** Row creation timestamp (ISO). */
  created_at: string;
  /** Row update timestamp (ISO). */
  updated_at: string;
}

/** A `campaign_automation_events` row. */
export interface AutomationEventRow {
  /** Event id (bigint identity, surfaced as number). */
  id: number;
  /** Parent campaign id. */
  campaign_id: string;
  /** The participant the event is about. */
  participant_id: string;
  /** The block id the event concerns, if any. */
  block_id: string | null;
  /** The block type the event concerns, if any. */
  block_type: string | null;
  /** Event status (sent, skipped, delivered, bounced, unsubscribed, etc.). */
  status: string;
  /** Provider message id for delivery correlation, if any. */
  provider_message_id: string | null;
  /** Error string on a failure, if any. */
  error: string | null;
  /** Structured event context. */
  detail: Record<string, unknown>;
  /** When the event was recorded (ISO). */
  executed_at: string;
}

/** A `campaign_automation_messages` row. */
export interface AutomationMessageRow {
  /** Message id (bigint identity, surfaced as number). */
  id: number;
  /** Parent campaign id. */
  campaign_id: string;
  /** Participant whose email thread contains this message. */
  participant_id: string;
  /** Automation event that transactionally produced this message. */
  source_event_id: number;
  /** Automation block associated with the message, when available. */
  block_id: string | null;
  /** Whether the message arrived from or was sent to the participant. */
  direction: 'inbound' | 'outbound';
  /** Product path that produced the message. */
  kind: 'automation' | 'participant_reply' | 'hollie_reply';
  /** Participant-facing sender label. */
  sender_name: string;
  /** Plain-text subject, when available. */
  subject: string | null;
  /** Meaningful plain-text body with no HTML or attachments. */
  body: string;
  /** Provider message id for support correlation, when available. */
  provider_message_id: string | null;
  /** When the message was sent or received (ISO). */
  sent_at: string;
}

/** One participant row to add via {@link addParticipants}. */
export interface AddParticipantInput {
  /** Email address (required). */
  email: string;
  /** Name, when known. */
  name?: string;
  /** Company, when known. */
  company?: string;
  /** Phone, when known. */
  phone?: string;
  /** IANA timezone, when known. */
  timezone?: string;
}

/** Server-preview command for manual or pasted participant rows. */
export interface PreviewManualParticipantImport {
  /** Discriminator for the non-mutating review phase. */
  action: 'preview';
  /** Flow version whose enabled sequence the editor reviewed. */
  reviewedFlowVersion?: number;
  /** Participant source parsed and bounded again by the server. */
  source: { kind: 'manual'; participants: AddParticipantInput[] };
}

/** Server-preview command carrying the actual CSV bytes. */
export interface PreviewCsvParticipantImport {
  /** Discriminator for the non-mutating review phase. */
  action: 'preview';
  /** Flow version whose enabled sequence the editor reviewed. */
  reviewedFlowVersion?: number;
  /** The browser file uploaded raw so the server owns its size and parsing. */
  source: { kind: 'csv'; file: File };
}

/** Id-only confirmation of an existing server preview. */
export interface ConfirmParticipantImport {
  /** Discriminator for the atomic activation phase. */
  action: 'confirm';
  /** Durable preview receipt selected by the user. */
  previewId: string;
}

/** Read-only reconciliation of one ambiguous preview confirmation. */
export interface ReconcileParticipantImport {
  /** Discriminator for receipt polling. */
  action: 'status';
  /** Stable preview receipt used by the original confirmation. */
  previewId: string;
}

/** Request durable cancellation after the reconciliation budget expires. */
export interface CancelParticipantImport {
  /** Discriminator for cancellation. */
  action: 'cancel';
  /** Stable preview receipt used by confirmation and cancellation. */
  previewId: string;
}

/** Every command accepted by {@link addParticipants}. */
export type ParticipantImportCommand =
  | PreviewManualParticipantImport
  | PreviewCsvParticipantImport
  | ConfirmParticipantImport
  | ReconcileParticipantImport
  | CancelParticipantImport;

/** Public, immutable server preview receipt. */
export interface ParticipantImportPreviewSample {
  /** Normalized email as parsed by the server. */
  email: string;
  /** Normalized optional name. */
  name: string | null;
  /** Normalized optional company. */
  company: string | null;
  /** Normalized optional phone. */
  phone: string | null;
  /** Validated IANA timezone, when supplied. */
  timezone: string | null;
}

/** Public, immutable server preview receipt. */
export interface ParticipantImportPreviewReceipt {
  /** Durable preview id used for confirmation and retry. */
  id: string;
  /** Preview lifecycle state. */
  status: 'previewed';
  /** Flow version pinned when this preview was created. */
  reviewed_flow_version: number;
  /** Hash of normalized semantic rows, excluding token material. */
  source_hash: string;
  /** Source parsed by the server. */
  source_kind: 'manual' | 'csv';
  /** Actual bytes received by the endpoint. */
  source_size_bytes: number;
  /** Exact number of rows frozen by the server. */
  requested_count: number;
  /** First three normalized rows frozen by the server. */
  sample: ParticipantImportPreviewSample[];
}

/** Result of the non-mutating server preview phase. */
export interface ParticipantImportPreviewResult {
  /** Response discriminator. */
  phase: 'preview';
  /** Server-issued review receipt. */
  receipt: ParticipantImportPreviewReceipt;
}

/** The per-row outcome returned by {@link addParticipants}. */
export interface AddParticipantOutcome {
  /** The (trimmed) email the outcome is for. */
  email: string;
  /** What happened to this row. */
  outcome: 'added' | 'duplicate' | 'invalid_email';
}

/** The full result of {@link addParticipants}. */
export interface AddParticipantsResult {
  /** Response discriminator. */
  phase: 'confirmed';
  /** Per-row outcomes, in the order the rows were submitted. */
  outcomes: AddParticipantOutcome[];
  /** Aggregate counts keyed by outcome. */
  counts: { added: number; duplicate: number; invalid_email: number };
  /** Dispatch state for a participant add accepted by an enabled automation flow. */
  dispatch?: { immediately_queued: number; recovery_scheduled: number };
  /** Durable operation receipt returned by the atomic import boundary. */
  receipt?: { id: string; status: 'completed'; idempotent: boolean };
}

/** Non-failure result returned while confirmation remains ambiguous. */
export interface ParticipantImportUnconfirmedResult {
  /** Response discriminator. */
  phase: 'unconfirmed';
  /** Stable ambiguity code. */
  error_code: 'operation_unconfirmed';
  /** Receipt retained for polling and idempotent retry. */
  receipt: { id: string; status: 'unconfirmed' };
}

/** Durable participant import cancellation. */
export interface ParticipantImportCancelledResult {
  /** Stable terminal error code. */
  error_code: 'operation_cancelled';
  /** Receipt identity retained for audit and safe retry. */
  receipt: { id: string; status: 'cancelled'; idempotent: boolean };
}

/** Result of either participant import phase. */
export type ParticipantImportResult =
  | ParticipantImportPreviewResult
  | AddParticipantsResult
  | ParticipantImportUnconfirmedResult
  | ParticipantImportCancelledResult;

/** Browser callback used by participant invite surfaces. */
export type AddParticipantsHandler = (
  command: ParticipantImportCommand,
) => Promise<ParticipantImportResult>;

/**
 * Load a campaign's automation flow, or null when none exists.
 *
 * @param campaignId - Parent campaign id.
 */
export async function getAutomationFlow(campaignId: string): Promise<AutomationFlowRow | null> {
  const { data, error } = await supabase
    .from('campaign_automation_flows')
    .select('id, campaign_id, enabled, blocks, version, default_timezone, created_at, updated_at')
    .eq('campaign_id', campaignId)
    .maybeSingle();
  if (error) {
    throw new Error(error.message || 'Failed to load automation flow');
  }
  return (data as AutomationFlowRow | null) ?? null;
}

/**
 * List a campaign's participants, newest first.
 *
 * @param campaignId - Parent campaign id.
 */
export async function listParticipants(campaignId: string): Promise<CampaignParticipantRow[]> {
  const { data, error } = await supabase
    .from('campaign_participants')
    .select(
      'id, campaign_id, interview_data_id, email, name, company, phone, timezone, status, unsubscribed_at, automation_paused, current_block_id, next_action_at, created_at, updated_at',
    )
    .eq('campaign_id', campaignId)
    .is('removed_at', null)
    .order('created_at', { ascending: false });
  if (error) {
    throw new Error(error.message || 'Failed to load participants');
  }
  return (data ?? []) as CampaignParticipantRow[];
}

/**
 * List a participant's automation events, newest first.
 *
 * @param participantId - The participant id.
 * @param limit - Maximum number of events to return.
 */
export async function listParticipantEvents(
  participantId: string,
  limit: number,
): Promise<AutomationEventRow[]> {
  const { data, error } = await supabase
    .from('campaign_automation_events')
    .select(
      'id, campaign_id, participant_id, block_id, block_type, status, provider_message_id, error, detail, executed_at',
    )
    .eq('participant_id', participantId)
    .order('executed_at', { ascending: false })
    .limit(limit);
  if (error) {
    throw new Error(error.message || 'Failed to load participant events');
  }
  return (data ?? []) as AutomationEventRow[];
}

/**
 * List a participant's plain-text message audit from oldest to newest.
 *
 * @param participantId - Participant row id.
 */
export async function listParticipantMessages(participantId: string): Promise<AutomationMessageRow[]> {
  const { data, error } = await supabase
    .from('campaign_automation_messages')
    .select(
      'id, campaign_id, participant_id, source_event_id, block_id, direction, kind, sender_name, subject, body, provider_message_id, sent_at',
    )
    .eq('participant_id', participantId)
    .order('sent_at', { ascending: true })
    .order('id', { ascending: true });
  if (error) {
    throw new Error(error.message || 'Failed to load participant messages');
  }
  return (data ?? []) as AutomationMessageRow[];
}

/** Build the JSON + bearer headers for an authed automation write. */
function authedHeaders(accessToken: string): Record<string, string> {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` };
}

/** Error returned by an automation endpoint, including its machine-readable code. */
export class AutomationServiceError extends Error {
  /** HTTP status returned by the endpoint. */
  readonly status: number;

  /** Stable endpoint error code, when supplied. */
  readonly errorCode?: string;

  /** Create a structured browser-service error. */
  constructor(message: string, status: number, errorCode?: string) {
    super(message);
    this.name = 'AutomationServiceError';
    this.status = status;
    this.errorCode = errorCode;
  }
}

/** Parsed failure body from an automation endpoint. */
interface EndpointErrorBody {
  /** Human-readable error. */
  error?: string;
  /** Stable machine-readable code. */
  error_code?: string;
  /** Durable operation status when the server can prove a safe failure. */
  operation?: { id?: string; status?: string };
  /** Durable participant receipt status when the server can prove a safe failure. */
  receipt?: { id?: string; status?: string };
}

/** Build a structured error from an already parsed response body. */
function endpointErrorFromBody(
  response: Response,
  prefix: string,
  data: EndpointErrorBody,
): AutomationServiceError {
  const detail = data.error || response.statusText || `status ${response.status}`;
  return new AutomationServiceError(`${prefix}: ${detail}`, response.status, data.error_code);
}

/** Build a structured error from a failed automation-endpoint response. */
async function endpointError(response: Response, prefix: string): Promise<AutomationServiceError> {
  const data = (await response.json().catch(() => ({}))) as EndpointErrorBody;
  return endpointErrorFromBody(response, prefix, data);
}

/** Build the non-failure result for an enable operation that still needs polling. */
function unconfirmedEnable(operationId: string): AutomationEnableUnconfirmed {
  return {
    error_code: 'operation_unconfirmed',
    operation: { id: operationId, status: 'unconfirmed' },
  };
}

/** Parse one enable response without treating an unproven 5xx as failure. */
async function parseEnableResponse(
  response: Response,
  operationId: string,
): Promise<AutomationEnableResult> {
  const data = (await response.json().catch(() => ({}))) as EndpointErrorBody;
  if (data.operation?.status === 'cancelled') return data as AutomationEnableCancelled;
  if (response.ok) return data as AutomationEnableResult;
  if (response.status >= 500 && data.operation?.status !== 'failed') {
    return unconfirmedEnable(operationId);
  }
  throw endpointErrorFromBody(response, 'Failed to enable automation', data);
}

/**
 * Enable automation on a campaign (one-click setup) and return the flow.
 *
 * @param campaignId - Parent campaign id.
 * @param timezone - The admin's browser IANA timezone (stored when valid).
 * @param accessToken - The caller's Supabase access token.
 * @param expectedFlowVersion - Existing flow version reviewed by the editor.
 */
export async function enableAutomation(
  campaignId: string,
  timezone: string | null,
  accessToken: string,
  operationId: string = crypto.randomUUID(),
  expectedFlowVersion?: number,
): Promise<AutomationEnableResult> {
  try {
    const response = await fetch('/api/automation/enable', {
      method: 'POST',
      headers: authedHeaders(accessToken),
      body: JSON.stringify({
        campaign_id: campaignId,
        timezone: timezone ?? undefined,
        operation_id: operationId,
        ...(expectedFlowVersion === undefined
          ? {}
          : { expected_flow_version: expectedFlowVersion }),
      }),
    });
    return await parseEnableResponse(response, operationId);
  } catch (error) {
    if (error instanceof AutomationServiceError) throw error;
    return unconfirmedEnable(operationId);
  }
}

/** Poll one enable receipt after a transport or post-commit ambiguity. */
export async function getAutomationEnableOperation(
  campaignId: string,
  operationId: string,
  accessToken: string,
): Promise<AutomationEnableResult> {
  try {
    const response = await fetch(
      `/api/automation/enable?campaign_id=${encodeURIComponent(campaignId)}&operation_id=${encodeURIComponent(operationId)}`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return await parseEnableResponse(response, operationId);
  } catch (error) {
    if (error instanceof AutomationServiceError) throw error;
    return unconfirmedEnable(operationId);
  }
}

/** Request durable cancellation for an expired enable operation. */
export async function cancelAutomationEnableOperation(
  campaignId: string,
  operationId: string,
  accessToken: string,
): Promise<AutomationEnableResult> {
  try {
    const response = await fetch(
      `/api/automation/enable?campaign_id=${encodeURIComponent(campaignId)}&operation_id=${encodeURIComponent(operationId)}`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return await parseEnableResponse(response, operationId);
  } catch (error) {
    if (error instanceof AutomationServiceError) throw error;
    return unconfirmedEnable(operationId);
  }
}

/**
 * Save an edited automation flow with an optimistic-concurrency version guard.
 *
 * @param campaignId - Parent campaign id.
 * @param blocks - The full edited block sequence.
 * @param version - The version the caller last read (compare-and-swap).
 * @param accessToken - The caller's Supabase access token.
 * @throws When the server rejects the write; an `edit_conflict` surfaces as a
 *   thrown error the caller can detect by message.
 */
export async function saveAutomationFlow(
  campaignId: string,
  blocks: AutomationBlock[],
  version: number,
  accessToken: string,
): Promise<AutomationFlowRow> {
  const response = await fetch('/api/automation/flow', {
    method: 'PUT',
    headers: authedHeaders(accessToken),
    body: JSON.stringify({ campaign_id: campaignId, blocks, version }),
  });
  if (!response.ok) {
    throw await endpointError(response, 'Failed to save automation flow');
  }
  return (await response.json()) as AutomationFlowRow;
}

/**
 * Enable or disable the whole flow without touching blocks (version-less).
 *
 * @param campaignId - Parent campaign id.
 * @param enabled - The desired enabled state.
 * @param accessToken - The caller's Supabase access token.
 */
export async function setAutomationEnabled(
  campaignId: string,
  enabled: boolean,
  accessToken: string,
): Promise<AutomationFlowRow> {
  const response = await fetch('/api/automation/flow', {
    method: 'PUT',
    headers: authedHeaders(accessToken),
    body: JSON.stringify({ campaign_id: campaignId, enabled }),
  });
  if (!response.ok) {
    throw await endpointError(response, 'Failed to update automation flow');
  }
  return (await response.json()) as AutomationFlowRow;
}

/**
 * Create or confirm a server-owned participant import preview.
 *
 * @param campaignId - Parent campaign id.
 * @param command - Raw preview source or id-only confirmation.
 * @param accessToken - The caller's Supabase access token.
 */
export async function addParticipants(
  campaignId: string,
  command: ParticipantImportCommand,
  accessToken: string,
): Promise<ParticipantImportResult> {
  if (command.action === 'preview' && command.source.kind === 'csv') {
    if (!Number.isInteger(command.reviewedFlowVersion) || command.reviewedFlowVersion! < 1) {
      throw new Error('Reviewed flow version is required');
    }
    const response = await fetch(
      `/api/automation/participants?action=preview&campaign_id=${encodeURIComponent(campaignId)}&reviewed_flow_version=${command.reviewedFlowVersion}`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'text/csv' },
        body: command.source.file,
      },
    );
    if (!response.ok) {
      throw await endpointError(response, 'Failed to preview participants');
    }
    return (await response.json()) as ParticipantImportPreviewResult;
  }

  if (command.action === 'status') {
    try {
      const response = await fetch(
        `/api/automation/participants?campaign_id=${encodeURIComponent(campaignId)}&preview_id=${encodeURIComponent(command.previewId)}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      const data = (await response.json().catch(() => ({}))) as EndpointErrorBody;
      if (data.receipt?.status === 'cancelled') return data as ParticipantImportCancelledResult;
      if (response.ok) return data as ParticipantImportResult;
      if (data.receipt?.status === 'failed' || response.status < 500) {
        throw endpointErrorFromBody(response, 'Failed to add participants', data);
      }
      return {
        phase: 'unconfirmed',
        error_code: 'operation_unconfirmed',
        receipt: { id: command.previewId, status: 'unconfirmed' },
      };
    } catch (error) {
      if (error instanceof AutomationServiceError) throw error;
      return {
        phase: 'unconfirmed',
        error_code: 'operation_unconfirmed',
        receipt: { id: command.previewId, status: 'unconfirmed' },
      };
    }
  }

  if (command.action === 'cancel') {
    return cancelParticipantImport(campaignId, command.previewId, accessToken);
  }

  let body: Record<string, unknown>;
  if (command.action === 'preview') {
    if (command.source.kind !== 'manual') throw new Error('Unsupported participant preview source');
    if (!Number.isInteger(command.reviewedFlowVersion) || command.reviewedFlowVersion! < 1) {
      throw new Error('Reviewed flow version is required');
    }
    body = {
        action: 'preview',
        campaign_id: campaignId,
        reviewed_flow_version: command.reviewedFlowVersion,
        source: { kind: 'manual' },
        participants: command.source.participants,
      };
  } else {
    body = {
        action: 'confirm',
        campaign_id: campaignId,
        preview_id: command.previewId,
      };
  }
  let response: Response;
  try {
    response = await fetch('/api/automation/participants', {
      method: 'POST',
      headers: authedHeaders(accessToken),
      body: JSON.stringify(body),
    });
  } catch (error) {
    if (command.action === 'confirm') {
      return addParticipants(campaignId, {
        action: 'status',
        previewId: command.previewId,
      }, accessToken);
    }
    throw error;
  }
  const data = (await response.json().catch(() => ({}))) as EndpointErrorBody;
  if (data.receipt?.status === 'cancelled') return data as ParticipantImportCancelledResult;
  if (response.ok) return data as ParticipantImportResult;
  if (
    command.action === 'confirm'
    && response.status >= 500
    && data.receipt?.status !== 'failed'
  ) {
    return addParticipants(campaignId, {
      action: 'status',
      previewId: command.previewId,
    }, accessToken);
  }
  throw endpointErrorFromBody(
    response,
    command.action === 'preview' ? 'Failed to preview participants' : 'Failed to add participants',
    data,
  );
}

/** Request durable cancellation for an expired participant confirmation. */
export async function cancelParticipantImport(
  campaignId: string,
  previewId: string,
  accessToken: string,
): Promise<ParticipantImportResult> {
  try {
    const response = await fetch(
      `/api/automation/participants?campaign_id=${encodeURIComponent(campaignId)}&preview_id=${encodeURIComponent(previewId)}`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${accessToken}` } },
    );
    const data = (await response.json().catch(() => ({}))) as EndpointErrorBody;
    if (data.receipt?.status === 'cancelled') return data as ParticipantImportCancelledResult;
    if (response.ok) return data as ParticipantImportResult;
    if (response.status >= 500) {
      return {
        phase: 'unconfirmed',
        error_code: 'operation_unconfirmed',
        receipt: { id: previewId, status: 'unconfirmed' },
      };
    }
    throw endpointErrorFromBody(response, 'Failed to cancel participant import', data);
  } catch (error) {
    if (error instanceof AutomationServiceError) throw error;
    return {
      phase: 'unconfirmed',
      error_code: 'operation_unconfirmed',
      receipt: { id: previewId, status: 'unconfirmed' },
    };
  }
}

/**
 * Remove a participant (their automation events cascade).
 *
 * @param id - The participant id.
 * @param accessToken - The caller's Supabase access token.
 */
export async function deleteParticipant(id: string, accessToken: string): Promise<void> {
  const response = await fetch(`/api/automation/participants/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authedHeaders(accessToken),
  });
  if (!response.ok) {
    throw await endpointError(response, 'Failed to remove participant');
  }
}

/**
 * Pause or resume automation for a single participant.
 *
 * @param id - The participant id.
 * @param paused - The desired paused state.
 * @param accessToken - The caller's Supabase access token.
 */
export async function setParticipantPaused(
  id: string,
  paused: boolean,
  accessToken: string,
): Promise<void> {
  const response = await fetch(`/api/automation/participants/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: authedHeaders(accessToken),
    body: JSON.stringify({ automation_paused: paused }),
  });
  if (!response.ok) {
    throw await endpointError(response, 'Failed to update participant');
  }
}
